import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { SecurityService } from '../services/security.service';
import { Router } from '@angular/router';

/**
 * Global Error Handler - Implements security best practices
 * Prevents sensitive information exposure in error messages
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: Error | HttpErrorResponse | any): void {
    const securityService = this.injector.get(SecurityService);
    const router = this.injector.get(Router);
    const zone = this.injector.get(NgZone);

    const chunkFailedMessage = /Loading chunk \d+ failed/g;

    if (error.message && chunkFailedMessage.test(error.message)) {
      // Handle chunk loading failures gracefully
      window.location.reload();
      return;
    }

    // Log the actual error to console for developers
    console.error('Unhandled Exception Captured by GlobalErrorHandler:', error);

    // Classify error severity and handle accordingly
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error, securityService);
    } else {
      this.handleClientError(error, securityService);

      // For unrecoverable client errors, navigate to 500 page
      zone.run(() => {
        router.navigate(['/error500'], { skipLocationChange: true });
      });
    }
  }

  /**
   * Handle HTTP errors
   */
  private handleHttpError(error: HttpErrorResponse, securityService: SecurityService): void {
    const errorLog = {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      timestamp: new Date().toISOString()
    };

    // Log security event
    securityService.logSecurityEvent('HTTP_ERROR', errorLog);

    // If it's a critical server error (500 range), we might want to redirect as well
    if (error.status >= 500) {
      const router = this.injector.get(Router);
      const zone = this.injector.get(NgZone);
      zone.run(() => {
        router.navigate(['/error500'], { skipLocationChange: true });
      });
    }
  }

  /**
   * Handle client errors
   */
  private handleClientError(error: Error, securityService: SecurityService): void {
    const errorLog = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    };

    // Log security event
    securityService.logSecurityEvent('CLIENT_ERROR', errorLog);

    // Send detailed error to logging service in production
    if (this.isProduction()) {
      this.sendErrorToLoggingService({
        ...errorLog,
        stack: this.sanitizeStackTrace(error.stack)
      });
    }
  }

  /**
   * Sanitize stack trace for logging
   */
  private sanitizeStackTrace(stack: string | undefined): string {
    if (!stack) return '';

    // Remove sensitive paths and personal information
    return stack
      .replace(/file:\/\/.*?\//g, '')
      .replace(/\/Users\/.*?\//g, '/[USER]/')
      .replace(/\/home\/.*?\//g, '/[USER]/');
  }

  /**
   * Send error to logging service
   */
  private sendErrorToLoggingService(errorLog: Record<string, any>): void {
    // This would typically send to a backend logging service
    // Example: this.http.post('/api/errors', errorLog).subscribe();
  }

  /**
   * Check if running in production
   */
  private isProduction(): boolean {
    return !window.location.hostname.includes('localhost');
  }
}
